import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AsyncBoundary } from '@kollez/design-system';
import { apiGet } from '@/lib/api';

interface OppRow {
  _id: string;
  title: string;
  kind?: string;
  employmentType?: string;
  applicationDeadline?: string;
  company?: { name?: string } | string;
}

function coName(c: OppRow['company']): string {
  if (!c) return '';
  return typeof c === 'string' ? c : c.name ?? '';
}

/**
 * /programs — hackathons, workshops, certifications, mentorship, training.
 *
 * Deep-audit fixes (Page 21/N):
 *   - Query URL asked for `type=Program,Hackathon,Certification`.
 *     `Program` is NOT a valid OpportunityType (see OPPORTUNITY_TYPES in
 *     @kollez/shared). The API silently ignored the bogus token, so the
 *     page only ever surfaced Hackathon+Certification and missed
 *     Workshop / Mentorship / Training entirely. Now uses only valid
 *     enum values.
 *   - Empty-state hint name-dropped specific programs ('Smart India
 *     Hackathon, NASSCOM FutureSkills, AWS student cohorts') as if
 *     Kollez surfaced those partnerships. It doesn't — the platform
 *     lists whatever partners publish. Rewrote to describe the shape
 *     without inventing brand affiliations.
 *   - 'Closed' vs 'Open' badge flipped to warning at days=0, but a
 *     programme is still open on its deadline day. Only < 0 is closed.
 */
export function StudentProgramsPage() {
  const query = useQuery({
    queryKey: ['student', 'programs'],
    queryFn: () => apiGet<{ rows: OppRow[] } | { opportunities: OppRow[] }>(
      '/opportunities?type=Hackathon,Workshop,Certification,Mentorship,Training&limit=25',
    ),
  });
  const raw = query.data as { rows?: OppRow[]; opportunities?: OppRow[] } | undefined;
  const rows = raw?.rows ?? raw?.opportunities ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Jobs</p>
          <h1 className="h-page">Hackathons and programmes</h1>
          <p className="subhead">Free programmes and challenges your placement office recommends. Winning one adds to your profile.</p>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
        emptyTitle="Nothing running right now"
        emptyHint="Hackathons, workshops, certifications and mentorship programmes published by partners of your college will appear here as they open."
      >
        <div className="grid gap-3">
          {rows.map((s) => {
            const deadline = s.applicationDeadline ? new Date(s.applicationDeadline) : null;
            const days = deadline ? Math.ceil((deadline.getTime() - Date.now()) / 86_400_000) : null;
            return (
              <Link key={s._id} to={`/opportunities/${s._id}`} className="card card-pad flex items-center gap-3" style={{ textDecoration: 'none' }}>
                <span className="logo-tile">{(coName(s.company) || s.title).slice(0, 2).toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <div className="cell-title">{s.title}</div>
                  <div className="cell-sub">
                    {s.employmentType ?? s.kind ?? 'Programme'}
                    {coName(s.company) ? ` · ${coName(s.company)}` : ''}
                    {deadline ? ` · ${days != null && days > 0 ? `Closes in ${days}d` : days === 0 ? 'Closes today' : 'Closed'}` : ''}
                  </div>
                </div>
                <span className={`badge ${days != null && days < 0 ? 'badge-warning' : 'badge-info'}`}>{days != null && days < 0 ? 'Closed' : 'Open'}</span>
              </Link>
            );
          })}
        </div>
      </AsyncBoundary>
    </div>
  );
}
